/**
 * SmsCampaignEditorPage.tsx
 *
 * Dedicated SMS campaign editor page at /marketing/sms-campaigns/:id.
 * Mirrors the PHP edit.php layout: two-panel (editor + phone preview),
 * header bar with status/actions, contact management, template picker,
 * send/schedule/test modals, and navigation guards.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send, MessageCircle, Trash2, Save, ArrowLeft,
  X, Phone, MoreVertical, Loader2, Copy,
  AlertTriangle, Users, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsMarketingTemplate } from '@/services/marketing.service';
import { SmsEditor, SmsPhoneMockup } from '@/components/sms-editor';
import SmsTemplatePickerModal from '@/components/sms-editor/SmsTemplatePickerModal';
import { calcCost, ensureSmsOptOut, hasSmsMessageContent, hasSmsOptOut, SMS_OPT_OUT_TOKEN } from '@/components/sms-editor/sms-editor-constants';
import { CloneCampaignModal } from './CloneCampaignModal';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { getTimezoneAbbreviation } from '@/lib/facilityTimezone';
import { DateTimePicker, nowAsDateTimeValue, toDateTimeValue } from '@/components/ui/DateTimePicker';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDateTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '\u2014';
  return parsed.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Page Component
// ═══════════════════════════════════════════════════════════════════════════

export default function SmsCampaignEditorPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const id = Number(idParam);
  const userTz = useUserTimezone();
  const tzAbbr = useMemo(() => getTimezoneAbbreviation(userTz), [userTz]);

  // ── Campaign data ──
  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id],
    queryFn: () => marketingSvc.getSmsCampaign(id),
    enabled: !!id,
  });

  // ── Local form state ──
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [excludeAccountUsers, setExcludeAccountUsers] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const baselineRef = useRef<Record<string, unknown>>({});

  // Initialize form from campaign
  useEffect(() => {
    if (campaign && !initialized) {
      const resolvedName = campaign.name ?? '';
      const resolvedDesc = campaign.description ?? '';
      const resolvedBody = campaign.messageBody ?? '';
      const resolvedMedia = campaign.mediaUrls ?? [];
      const parsedScheduled = campaign.scheduledAt ? new Date(campaign.scheduledAt) : null;
      const resolvedScheduled = parsedScheduled ? toDateTimeValue(parsedScheduled) : '';

      setCampaignName(resolvedName);
      setCampaignDescription(resolvedDesc);
      setMessageBody(resolvedBody);
      setMediaUrls(resolvedMedia);
      setExcludeAccountUsers(!!campaign.accountUsers);
      setScheduledAt(resolvedScheduled);

      baselineRef.current = {
        name: resolvedName, description: resolvedDesc, messageBody: resolvedBody,
        mediaUrls: JSON.stringify(resolvedMedia), scheduledAt: resolvedScheduled,
      };
      setInitialized(true);
    }
  }, [campaign, initialized]);

  const hasUnsaved = useMemo(() => {
    if (!campaign || !initialized) return false;
    const b = baselineRef.current;
    if (campaignName !== b.name) return true;
    if (campaignDescription !== b.description) return true;
    if (messageBody !== b.messageBody) return true;
    if (JSON.stringify(mediaUrls) !== b.mediaUrls) return true;
    if (scheduledAt !== b.scheduledAt) return true;
    return false;
  }, [campaign, initialized, campaignName, campaignDescription, messageBody, mediaUrls, scheduledAt]);

  // ── Lists ──
  const { data: attachedLists = [], refetch: refetchLists } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'lists'],
    queryFn: () => marketingSvc.getSmsCampaignLists(id),
    enabled: !!campaign,
  });

  const { data: availableLists = [], refetch: refetchAvailableLists } = useQuery({
    queryKey: ['marketing', 'lists-all'],
    queryFn: () => marketingSvc.listAudienceLists({ perPage: 500 }).then(r => r.data),
    enabled: !!campaign,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // ── Recipient count ── (matches email campaign pattern: manual state + explicit fetch)
  const [totalContacts, setTotalContacts] = useState(0);
  const [recipientLoading, setRecipientLoading] = useState(true);
  const refreshRecipientCount = useCallback(async () => {
    if (!id) return;
    setRecipientLoading(true);
    try {
      const data = await marketingSvc.getSmsCampaignRecipientCount(id);
      setTotalContacts(data.count ?? 0);
    } catch { /* ignore */ }
    setRecipientLoading(false);
  }, [id]);

  useEffect(() => {
    if (campaign) refreshRecipientCount();
  }, [campaign, refreshRecipientCount]);

  // ── Direct contact / exclude counts ──
  const { data: directContactCount = 0, refetch: refetchDirectContactCount } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'direct-contact-count'],
    queryFn: () => marketingSvc.getSmsCampaignDirectContactCount(id),
    enabled: !!campaign,
  });
  const { data: excludeContactCount = 0, refetch: refetchExcludeContactCount } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'exclude-contact-count'],
    queryFn: () => marketingSvc.getSmsCampaignExcludeContactCount(id),
    enabled: !!campaign,
  });

  // ── Modals ──
  const [showTestModal, setShowTestModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const [testPhoneDigits, setTestPhoneDigits] = useState('');
  const [listTypeFilter, setListTypeFilter] = useState<'static' | 'dynamic'>('static');

  // ── Derived ──
  const hasMedia = mediaUrls.length > 0;
  const cost = calcCost(totalContacts, messageBody, hasMedia);

  // ── Schedule floor ──
  // Re-derived on a timer so a date that was valid when picked starts failing
  // once it slips into the past, rather than staying enabled until reload. A
  // campaign can sit open for a while before anyone presses Schedule Now.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!scheduledAt) return;
    const t = setInterval(() => setNowTick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, [scheduledAt]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minScheduleAt = useMemo(() => nowAsDateTimeValue(userTz), [userTz, nowTick]);
  const scheduleInPast = !!scheduledAt && scheduledAt < minScheduleAt;

  // ── Send/schedule validation ──
  // Anything listed here stops the send; the header pill and the review modal
  // both show the same list so the reason is never a mystery.
  const sendBlockers = useMemo(() => {
    const blockers: string[] = [];
    if (hasUnsaved) blockers.push('You have unsaved changes — save them before sending.');
    if (!campaignName.trim()) blockers.push('The campaign needs a name.');
    // Opt-out instructions are the SMS twin of the email unsubscribe link.
    // Saving adds the token, so this only bites on a message written elsewhere
    // (legacy UI, direct API) and opened here untouched; the API refuses the
    // send and schedule too.
    if (!hasSmsMessageContent(messageBody)) blockers.push('The message has no content.');
    else if (!hasSmsOptOut(messageBody)) blockers.push(`The message has no opt-out instructions — saving adds ${SMS_OPT_OUT_TOKEN} to the end for you.`);
    if (totalContacts <= 0) blockers.push('This campaign has no recipients.');
    if (scheduleInPast) blockers.push('The scheduled send date is in the past.');
    return blockers;
  }, [hasUnsaved, campaignName, messageBody, totalContacts, scheduleInPast]);
  const hasBlockers = sendBlockers.length > 0;
  const isReady = !hasBlockers && !recipientLoading;
  const [showIssues, setShowIssues] = useState(false);
  const issuesRef = useRef<HTMLDivElement>(null);

  // Every save goes out with opt-out instructions: the token is appended when
  // the message has none, the way the email editors add the unsubscribe
  // footer. Returns the body that is saved so the baseline can match it; the
  // toast is the only sign anything was added.
  const bodyForSave = useCallback((): string => {
    const body = ensureSmsOptOut(messageBody);
    if (body !== messageBody) {
      setMessageBody(body);
      toast.info('Opt-out instructions added', `${SMS_OPT_OUT_TOKEN} was added to the end of the message.`);
    }
    return body;
  }, [messageBody, toast]);

  // ── Mutations ──
  const saveMut = useMutation({
    mutationFn: (body: string) =>
      marketingSvc.updateSmsCampaign(id, {
        name: campaignName,
        description: campaignDescription,
        messageBody: body,
        mediaUrls,
        scheduledAt: scheduledAt || null,
        accountUsers: excludeAccountUsers,
      }),
    onSuccess: (_saved, body) => {
      baselineRef.current = {
        name: campaignName, description: campaignDescription, messageBody: body,
        mediaUrls: JSON.stringify(mediaUrls), scheduledAt,
      };
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      toast.success('Campaign saved');
    },
    onError: () => toast.error('Failed to save campaign'),
  });

  const sendMut = useMutation({
    mutationFn: () => marketingSvc.sendSmsCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      toast.success('Campaign sent!');
      setTimeout(() => navigate('/marketing/sms-campaigns'), 1000);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Send failed'),
  });

  const scheduleMut = useMutation({
    mutationFn: () => marketingSvc.scheduleSmsCampaign(id, scheduledAt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      toast.success('Campaign scheduled!');
      navigate(`/marketing/sms-campaigns/${id}/scheduled`, { replace: true });
    },
    onError: (err: any) => {
      // Surface the API's reason (e.g. the past-date backstop) rather than a generic line.
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || 'Failed to schedule';
      toast.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => marketingSvc.deleteSmsCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      toast.success('Campaign deleted');
      navigate('/marketing/sms-campaigns');
    },
    onError: () => toast.error('Failed to delete campaign'),
  });
  const handleDelete = useCallback(() => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;
    deleteMut.mutate();
  }, [deleteMut]);

  // ── Clone campaign ──
  const cloneMut = useMutation({
    mutationFn: (cloneName: string) => marketingSvc.cloneSmsCampaign(id, cloneName),
    onSuccess: (data) => {
      toast.success('Campaign cloned successfully');
      setShowCloneModal(false);
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      navigate(`/marketing/sms-campaigns/${data.smsCampaignId}`);
    },
    onError: () => toast.error('Failed to clone campaign'),
  });

  const testMut = useMutation({
    mutationFn: (phone: string) => marketingSvc.sendTestSms(id, phone),
    onSuccess: () => { toast.success('Test SMS sent!'); setShowTestModal(false); setTestPhoneDigits(''); },
    onError: () => toast.error('Test send failed'),
  });

  const addListMut = useMutation({
    mutationFn: (listId: number) => marketingSvc.addSmsCampaignList(id, listId),
    onSuccess: (data) => {
      refetchLists();
      if (data?.totalRecipients != null) setTotalContacts(data.totalRecipients);
      else refreshRecipientCount();
    },
    onError: () => {
      toast.error('Failed to add list');
      refreshRecipientCount();
    },
  });

  const removeListMut = useMutation({
    mutationFn: (listId: number) => marketingSvc.removeSmsCampaignList(id, listId),
    onSuccess: (data) => {
      refetchLists();
      if (data?.totalRecipients != null) setTotalContacts(data.totalRecipients);
      else refreshRecipientCount();
    },
    onError: () => {
      toast.error('Failed to remove list');
      refreshRecipientCount();
    },
  });

  const [accountUsersToggling, setAccountUsersToggling] = useState(false);
  const handleToggleAccountUsers = useCallback(async (checked: boolean) => {
    setExcludeAccountUsers(checked);
    setAccountUsersToggling(true);
    try {
      await marketingSvc.toggleSmsAccountUsers(id, checked);
      refreshRecipientCount();
    } catch {
      toast.error('Failed to toggle account users');
      setExcludeAccountUsers(!checked);
    }
    setAccountUsersToggling(false);
  }, [id, refreshRecipientCount, toast]);

  // Send / Schedule flow
  const handleSendOrSchedule = () => {
    setShowReviewModal(true);
  };

  const confirmSendOrSchedule = async () => {
    // The modal can sit open long enough for the schedule to slip into the past.
    if (hasBlockers) {
      toast.error(sendBlockers[0]);
      return;
    }
    setShowReviewModal(false);
    const body = bodyForSave();
    try {
      await marketingSvc.updateSmsCampaign(id, {
        name: campaignName,
        description: campaignDescription,
        messageBody: body,
        mediaUrls,
        scheduledAt: scheduledAt || null,
        accountUsers: excludeAccountUsers,
      });
      baselineRef.current = {
        name: campaignName, description: campaignDescription, messageBody: body,
        mediaUrls: JSON.stringify(mediaUrls), scheduledAt,
      };
    } catch {
      toast.error('Save failed, aborting send');
      return;
    }
    if (scheduledAt) {
      scheduleMut.mutate();
    } else {
      sendMut.mutate();
    }
  };

  // ── Available lists for picker ──
  const attachedListIds = new Set(attachedLists.map(l => l.emailListId));
  const filteredAvailableLists = availableLists.filter(
    (l) => l.active && !attachedListIds.has(l.emailListId) && l.listType === listTypeFilter,
  );
  const listOptions = filteredAvailableLists.map((l) => ({
    value: l.emailListId,
    label: `${l.name} (${l.listType === 'dynamic' ? 'Dynamic' : 'Static'}) -- ${(l.uniquePhoneCount ?? l.patientCount).toLocaleString()} phones`,
  }));

  // ── Template picker handler ──
  const handleTemplateApply = useCallback((tpl: SmsMarketingTemplate) => {
    const hasExisting = messageBody.trim().length > 0 || mediaUrls.length > 0;
    if (hasExisting && !window.confirm(`Replace current content with the template "${tpl.name}"?`)) return;
    // A template saved before the opt-out rule may lack the token; add it as
    // the content arrives so the editor never shows a message without it.
    setMessageBody(ensureSmsOptOut(tpl.messageBody ?? ''));
    setMediaUrls(tpl.mediaUrls ?? []);
    setShowTemplatePickerModal(false);
  }, [messageBody, mediaUrls]);

  // ── Navigation: back button ──
  const handleBack = useCallback(() => {
    navigate('/marketing/sms-campaigns');
  }, [navigate]);

  // ── beforeunload guard ──
  useUnsavedChangesGuard(hasUnsaved);

  // ── Close overflow menu on outside click ──
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);

  // ── Close the send-warnings popover on outside click ──
  useEffect(() => {
    if (!showIssues) return;
    const handler = (e: MouseEvent) => {
      if (issuesRef.current && !issuesRef.current.contains(e.target as Node)) {
        setShowIssues(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showIssues]);

  // ── Visibility refresh (refetch counts when tab regains focus) ──
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) {
        refreshRecipientCount();
        refetchLists();
        refetchAvailableLists();
        refetchDirectContactCount();
        refetchExcludeContactCount();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refreshRecipientCount, refetchLists, refetchAvailableLists, refetchDirectContactCount, refetchExcludeContactCount]);

  // ── Loading / Not found ──
  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16 text-slate-500">
        Campaign not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={() => navigate('/marketing/sms-campaigns')}>Back to Campaigns</Button>
        </div>
      </div>
    );
  }

  // A scheduled campaign is locked: it gets a read-only page whose only action
  // is Cancel Send. Anything already sent belongs on its stats page.
  if (campaign.status === 'scheduled') {
    navigate(`/marketing/sms-campaigns/${id}/scheduled`, { replace: true });
    return null;
  }
  if (campaign.status === 'sent' || campaign.status === 'sending') {
    navigate(`/marketing/sms-campaigns/${id}/stats`, { replace: true });
    return null;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold text-slate-800">Edit Campaign: {campaign.name}</span>
          <Badge variant={STATUS_BADGE[campaign.status] ?? 'neutral'} dot>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsaved && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 border border-amber-300 bg-amber-50 rounded px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          )}

          <Button variant="ghost" size="sm" onClick={() => setShowTestModal(true)}>
            <Phone className="h-4 w-4 mr-1" /> Send Test
          </Button>

          {/* Why this campaign can't be sent yet (mirrors the email editor) */}
          {hasBlockers && (
            <div className="relative" ref={issuesRef}>
              <button
                type="button"
                onClick={() => setShowIssues(v => !v)}
                title={`${sendBlockers.length} thing${sendBlockers.length === 1 ? '' : 's'} to fix before sending`}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"
              >
                <AlertTriangle className="h-4 w-4" />
                {sendBlockers.length}
              </button>

              {showIssues && (
                <div className="absolute right-0 top-full z-30 mt-1.5 w-80 rounded-md border border-slate-200 bg-white p-3 text-left shadow-lg">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Must fix before sending
                  </div>
                  <ul className="mt-1.5 space-y-1.5">
                    {sendBlockers.map(msg => (
                      <li key={msg} className="flex gap-1.5 text-sm text-slate-700">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                        {msg}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            disabled={!isReady}
            title={hasBlockers ? 'Check the warnings beside this button' : undefined}
            onClick={handleSendOrSchedule}
          >
            <Send className="h-4 w-4 mr-1" />
            {scheduledAt ? 'Schedule Now' : 'Send Now'}
          </Button>

          <Button size="sm" loading={saveMut.isPending} onClick={() => saveMut.mutate(bodyForSave())}>
            <Save className="h-4 w-4 mr-1" /> Save Changes
          </Button>

          <div className="relative" ref={overflowRef}>
            <button
              type="button"
              onClick={() => setShowOverflowMenu(p => !p)}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {showOverflowMenu && (
              <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => { setShowOverflowMenu(false); setShowCloneModal(true); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Copy className="h-4 w-4" />
                  Clone Campaign
                </button>
                {campaign.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => { setShowOverflowMenu(false); handleDelete(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Campaign
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main layout: editor + phone preview */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-[3] overflow-y-auto">
          {/* Sticky header inside left panel */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">SMS Campaign Details</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">Unique Contacts</span>
                  {recipientLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
                  ) : (
                    <span className="text-2xl font-bold text-sky-500">{totalContacts.toLocaleString()}</span>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs text-slate-500">Est. Cost:</span>
                  <span className="text-lg font-bold text-orange-500">${cost.total.toFixed(2)}</span>
                </div>
                <span className="text-[11px] text-slate-400">{cost.label}</span>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
          {/* Campaign Name */}
          <FormField label="Campaign Name" required>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Enter campaign name"
            />
          </FormField>

          {/* Message Content label + template button */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-slate-700">Message Content</label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTemplatePickerModal(true)}
            >
              <FileText className="h-4 w-4 mr-1" /> Use Template
            </Button>
          </div>

          {/* SMS Editor */}
          <SmsEditor
            messageBody={messageBody}
            onMessageChange={setMessageBody}
            mediaUrls={mediaUrls}
            onMediaChange={setMediaUrls}
          />

          {/* Description */}
          <FormField label="Description" helpText="For internal reference only -- not visible to recipients">
            <Textarea autoResize
              value={campaignDescription}
              onChange={(e) => setCampaignDescription(e.target.value)}
              rows={2}
              placeholder="Internal notes about this campaign..."
            />
          </FormField>

          {/* Schedule */}
          <FormField label={`Schedule Send (${tzAbbr})`} helpText="Leave blank to send manually. Set a date and time to enable scheduling." htmlFor="sms-schedule-at">
            <DateTimePicker
              id="sms-schedule-at"
              value={scheduledAt}
              min={minScheduleAt}
              onChange={setScheduledAt}
            />
          </FormField>

          {/* Campaign Contacts Section */}
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-2">Campaign Contacts</label>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Direct contacts */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" style={{ background: '#f0f7ff' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-sky-500">{directContactCount}</span>
                    <span className="text-sm font-semibold text-slate-700">direct contact(s)</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Added directly to this campaign</p>
                </div>
                <Link to={`/marketing/sms-campaigns/${id}/contacts`} className="text-sm font-medium text-blue-600 hover:underline">
                  View / Edit
                </Link>
              </div>

              {/* Lists section */}
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl font-bold text-indigo-500">{attachedLists.length}</span>
                  <span className="text-sm font-semibold text-slate-700">
                    contact {attachedLists.length === 1 ? 'list' : 'lists'}
                  </span>
                </div>
                <span className="text-xs text-slate-500 italic block mb-3">
                  Attach reusable lists to include their members when sending
                </span>

                {/* List type filter + picker */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setListTypeFilter('static')}
                      className={cn(
                        'px-2 py-1 text-xs font-semibold rounded border transition-colors',
                        listTypeFilter === 'static'
                          ? 'bg-blue-50 border-blue-600 text-blue-700'
                          : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100',
                      )}
                    >
                      Static
                    </button>
                    <button
                      onClick={() => setListTypeFilter('dynamic')}
                      className={cn(
                        'px-2 py-1 text-xs font-semibold rounded border transition-colors',
                        listTypeFilter === 'dynamic'
                          ? 'bg-orange-50 border-orange-500 text-orange-700'
                          : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100',
                      )}
                    >
                      Dynamic
                    </button>
                  </div>
                  <div className="flex-1">
                    <Select
                      placeholder={`Search and select a ${listTypeFilter} list...`}
                      options={listOptions}
                      value={null}
                      onChange={(val) => { if (val) addListMut.mutate(val); }}
                      searchable
                      clearable
                    />
                  </div>
                </div>

                {/* Attached lists */}
                {attachedLists.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-1">No lists attached to this campaign</p>
                ) : (
                  <div className="space-y-1.5">
                    {attachedLists.map((li) => (
                      <div
                        key={li.emailListId}
                        className="flex items-center justify-between px-3 py-2 border border-slate-100 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold text-slate-700 truncate">{li.listName}</span>
                          <span
                            className={cn(
                              'text-[11px] font-semibold px-2 py-0.5 rounded-full',
                              li.listType === 'dynamic'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 text-blue-700',
                            )}
                          >
                            {li.listType === 'dynamic' ? 'Dynamic' : 'Static'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-slate-500">{(li.uniquePhoneCount ?? li.memberCount).toLocaleString()} phones</span>
                          <button
                            onClick={() => removeListMut.mutate(li.emailListId)}
                            className="text-slate-300 hover:text-red-500 hover:bg-red-50 rounded p-0.5 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Exclude contacts */}
              <div className="px-4 py-3 border-b border-slate-200" style={{ background: '#fff8f8' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-red-500">{excludeContactCount}</span>
                      <span className="text-sm font-semibold text-slate-700">excluded contact(s)</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">These phone numbers will be excluded from sending</p>
                  </div>
                  <Link to={`/marketing/sms-campaigns/${id}/excludes`} className="text-sm font-medium text-blue-600 hover:underline">
                    View / Edit
                  </Link>
                </div>
              </div>

              {/* Exclude account users */}
              <div className="px-4 py-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeAccountUsers}
                    onChange={(e) => handleToggleAccountUsers(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500"
                  />
                  <span className="text-sm font-semibold text-slate-600">Exclude account users from this campaign</span>
                  {accountUsersToggling && <Spinner className="h-3 w-3" />}
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-7">
                  When checked, phone numbers belonging to users on this account will be automatically excluded when sending
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Direct contacts and list members will be merged when sending. Excluded contacts and account users (if selected) are removed before send.
            </p>
          </div>
          </div>
        </div>

        {/* Phone Preview Panel */}
        <div className="flex-[2] bg-slate-100 flex flex-col items-center justify-start pt-6 overflow-y-auto">
          <div className="text-center mb-3 text-sm font-semibold text-slate-500">Preview</div>
          <SmsPhoneMockup
            messageBody={messageBody}
            mediaUrls={mediaUrls}
            headerText="Message Preview"
            appendOptOut
          />
        </div>
      </div>

      {/* ── Test SMS Modal ── */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)} title="Send Test SMS" size="sm">
        <div className="space-y-4">
          <FormField label="Phone Number" helpText="Enter the 10-digit phone number to receive the test message">
            <Input
              value={
                testPhoneDigits.length === 10
                  ? `(${testPhoneDigits.slice(0, 3)}) ${testPhoneDigits.slice(3, 6)}-${testPhoneDigits.slice(6)}`
                  : testPhoneDigits
              }
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                setTestPhoneDigits(digits);
              }}
              placeholder="(555) 123-4567"
              onKeyDown={(e) => { if (e.key === 'Enter' && testPhoneDigits.length === 10) { e.preventDefault(); testMut.mutate(testPhoneDigits); } }}
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowTestModal(false)}>Cancel</Button>
            <Button
              loading={testMut.isPending}
              disabled={testPhoneDigits.length !== 10}
              onClick={() => testMut.mutate(testPhoneDigits)}
            >
              <Phone className="h-4 w-4 mr-1" /> Send Test
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Review & Send/Schedule Modal ── */}
      <Modal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        title={scheduledAt ? 'Review & Schedule Campaign' : 'Review & Send Campaign'}
        size="lg"
      >
        <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-sky-500" />
                {recipientLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
                ) : (
                  <span className="text-2xl font-bold text-sky-500">{totalContacts.toLocaleString()}</span>
                )}
                <span className="text-sm font-semibold text-slate-500">contacts</span>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-sm text-slate-500">Est. Cost:</span>
                  <span className="text-xl font-bold text-orange-500">${cost.total.toFixed(2)}</span>
                </div>
                <span className="text-[11px] text-slate-400">{cost.label}</span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>This action cannot be undone.</strong>{' '}
                {scheduledAt
                  ? 'Once scheduled, this campaign will be delivered to all recipients at the scheduled time.'
                  : 'Once sent, this campaign will be delivered to all recipients immediately.'}
              </span>
            </div>

            {/* The button that opened this modal was enabled, but things can change
                while it sits open (a schedule time slipping into the past, for one). */}
            {hasBlockers && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Must fix before sending
                </div>
                <ul className="mt-1.5 space-y-1">
                  {sendBlockers.map(msg => (
                    <li key={msg} className="flex gap-1.5">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-2 font-semibold text-slate-500 w-32">Campaign</td>
                  <td className="py-2 text-slate-800">{campaignName}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 font-semibold text-slate-500">Type</td>
                  <td className="py-2 text-slate-800">{hasMedia ? 'MMS' : 'SMS'}</td>
                </tr>
                {scheduledAt && (
                  <tr className="border-b border-slate-100">
                    <td className="py-2 font-semibold text-slate-500">Scheduled For</td>
                    <td className="py-2 text-slate-800">{fmtDateTime(scheduledAt)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setShowReviewModal(false)}>Cancel</Button>
              <Button
                className={scheduledAt ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'}
                loading={sendMut.isPending || scheduleMut.isPending}
                disabled={hasBlockers}
                title={hasBlockers ? 'Fix the items listed above first' : undefined}
                onClick={confirmSendOrSchedule}
              >
                <Send className="h-4 w-4 mr-1" />
                {scheduledAt ? 'Schedule Now' : 'Send Now'}
              </Button>
            </div>
          </div>

          <div className="w-64 flex-shrink-0 flex flex-col items-center">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Message Preview</div>
            <SmsPhoneMockup
              messageBody={messageBody}
              mediaUrls={mediaUrls}
              headerText="SMS Preview"
              compact
              appendOptOut
            />
          </div>
        </div>
      </Modal>

      {/* ── Template Picker Modal ── */}
      <SmsTemplatePickerModal
        isOpen={showTemplatePickerModal}
        onClose={() => setShowTemplatePickerModal(false)}
        onApply={handleTemplateApply}
      />

      <CloneCampaignModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={(cloneName) => cloneMut.mutate(cloneName)}
        loading={cloneMut.isPending}
      />

    </div>
  );
}

